import { useMemo, useState, type ReactNode } from "react";
import { CalendarDays, Inbox } from "lucide-react";
import { BrandMark } from "@/components/brand-mark";
import { StatusBadge } from "@/components/status/status-badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton, Skeleton } from "@/components/ui/skeleton";
import { useTheme, type ThemeMode } from "@/lib/theme";
import type { StatusTone } from "@/lib/types";

const LIGHT_SWATCHES = [
  {
    label: "Page canvas",
    token: "--rs-bg",
    hex: "#f8faff",
    requirement: "App background (`bg-rs-bg`).",
  },
  {
    label: "Elevated surface",
    token: "--rs-bg-elevated",
    hex: "#fcfcfd",
    requirement: "Panels, dialogs, inputs (`bg-rs-elevated`).",
  },
  {
    label: "Muted surface",
    token: "--rs-bg-muted",
    hex: "#f0f4fa",
    requirement: "Secondary strips, skeletons (`bg-rs-muted`).",
  },
  {
    label: "Sidebar",
    token: "--rs-bg-sidebar",
    hex: "#fbfcff",
    requirement: "Workspace sidebar (`bg-rs-sidebar`).",
  },
  {
    label: "Ink",
    token: "--rs-ink",
    hex: "#101b36",
    requirement: "Primary text (`text-rs-ink`).",
  },
  {
    label: "Muted ink",
    token: "--rs-ink-muted",
    hex: "#5b677a",
    requirement: "Supporting copy (`text-rs-ink-muted`).",
  },
  {
    label: "Subtle ink",
    token: "--rs-ink-subtle",
    hex: "#8a95a8",
    requirement: "Meta, placeholders (`text-rs-ink-subtle`).",
  },
  {
    label: "Border",
    token: "--rs-border",
    hex: "#e5eaf2",
    requirement: "Dividers and field borders.",
  },
  {
    label: "Strong border",
    token: "--rs-border-strong",
    hex: "#b8c4d4",
    requirement: "Emphasized outlines (`border-rs-border-strong`).",
  },
  {
    label: "Accent",
    token: "--rs-accent",
    hex: "#2563eb",
    requirement: "Primary CTAs, links, focus (`bg-rs-accent` / `--rs-focus`).",
  },
  {
    label: "Accent hover",
    token: "--rs-accent-hover",
    hex: "#1d4ed8",
    requirement: "Primary button hover.",
  },
  {
    label: "Accent soft",
    token: "--rs-accent-soft",
    hex: "#dbeafe",
    requirement: "Selected nav / soft chips (`bg-rs-accent-soft`).",
  },
  {
    label: "Accent ink",
    token: "--rs-accent-ink",
    hex: "#1e3a8a",
    requirement: "Text on accent-soft (`text-rs-accent-ink`).",
  },
  {
    label: "On accent",
    token: "--rs-on-accent",
    hex: "#fafbff",
    requirement:
      "Text/icons on solid accent & danger buttons (`text-rs-on-accent`).",
  },
  {
    label: "Success",
    token: "--rs-success",
    hex: "#15803d",
    requirement: "Success text / accents.",
  },
  {
    label: "Success soft",
    token: "--rs-success-soft",
    hex: "#dcfce7",
    requirement: "Success badge / alert fill.",
  },
  {
    label: "Warning",
    token: "--rs-warning",
    hex: "#c2410c",
    requirement: "Warning text / accents.",
  },
  {
    label: "Warning soft",
    token: "--rs-warning-soft",
    hex: "#ffedd5",
    requirement: "Warning banners / badges.",
  },
  {
    label: "Danger",
    token: "--rs-danger",
    hex: "#b91c1c",
    requirement: "Destructive actions / errors.",
  },
  {
    label: "Danger soft",
    token: "--rs-danger-soft",
    hex: "#fee2e2",
    requirement: "Danger badges / soft alerts.",
  },
  {
    label: "Info",
    token: "--rs-info",
    hex: "#1d4ed8",
    requirement: "Informational accents.",
  },
  {
    label: "Info soft",
    token: "--rs-info-soft",
    hex: "#dbeafe",
    requirement: "Info badges / soft alerts.",
  },
] as const;

const DARK_HEX: Record<(typeof LIGHT_SWATCHES)[number]["token"], string> = {
  "--rs-bg": "#181b23",
  "--rs-bg-elevated": "#222733",
  "--rs-bg-muted": "#1c2029",
  "--rs-bg-sidebar": "#1a1e27",
  "--rs-ink": "#ededec",
  "--rs-ink-muted": "#a3b0c2",
  "--rs-ink-subtle": "#7a8799",
  "--rs-border": "#2a3544",
  "--rs-border-strong": "#3d4b5e",
  "--rs-accent": "#60a5fa",
  "--rs-accent-hover": "#93c5fd",
  "--rs-accent-soft": "#1e3a5f",
  "--rs-accent-ink": "#bfdbfe",
  "--rs-on-accent": "#181b23",
  "--rs-success": "#4ade80",
  "--rs-success-soft": "#14532d",
  "--rs-warning": "#fb923c",
  "--rs-warning-soft": "#7c2d12",
  "--rs-danger": "#f87171",
  "--rs-danger-soft": "#7f1d1d",
  "--rs-info": "#60a5fa",
  "--rs-info-soft": "#1e3a5f",
};

const STATUS_TONES: StatusTone[] = [
  "ready",
  "scheduled",
  "syncing",
  "synced",
  "needs_configuration",
  "warning",
  "failed",
  "completed",
  "paused",
  "auth_required",
  "neutral",
];

const SECTIONS = [
  { id: "sg-colors", label: "Colors" },
  { id: "sg-typography", label: "Typography" },
  { id: "sg-buttons", label: "Buttons" },
  { id: "sg-action-layout", label: "Action Placement" },
  { id: "sg-forms", label: "Forms" },
  { id: "sg-alerts", label: "Alerts" },
  { id: "sg-cards", label: "Cards / Panels" },
  { id: "sg-elevation", label: "Elevation" },
  { id: "sg-table", label: "Tables" },
  { id: "sg-badges", label: "Badges" },
  { id: "sg-empty", label: "Empty" },
  { id: "sg-loading", label: "Loading" },
  { id: "sg-dialog-shell", label: "Dialog Shell" },
  { id: "sg-guided-setup", label: "Guided Setup" },
  { id: "sg-navigation", label: "Navigation" },
  { id: "sg-brand", label: "Brand" },
  { id: "sg-auth", label: "Auth" },
  { id: "sg-a11y", label: "Accessibility" },
] as const;

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      data-testid={`style-guide-${id}`}
      className="scroll-mt-24 border-t border-rs-border pt-10"
    >
      <h2 className="text-lg font-semibold tracking-tight text-rs-ink">
        {title}
      </h2>
      <div className="mt-5 space-y-5">{children}</div>
    </section>
  );
}

function Swatch({
  label,
  token,
  hex,
  requirement,
}: {
  label: string;
  token: string;
  hex: string;
  requirement: string;
}) {
  return (
    <div className="overflow-hidden rounded-rs-sm border border-rs-border bg-rs-elevated">
      <div
        className="h-14 border-b border-rs-border"
        style={{ background: hex }}
      />
      <div className="space-y-1 p-3">
        <p className="text-sm font-medium text-rs-ink">{label}</p>
        <p className="font-mono text-[11px] text-rs-ink-muted">{token}</p>
        <p className="font-mono text-[11px] text-rs-ink">{hex}</p>
        <p className="text-xs text-rs-ink-muted">{requirement}</p>
      </div>
    </div>
  );
}

function AlertDemo({
  tone,
  title,
  body,
}: {
  tone: "info" | "warning" | "success" | "error";
  title: string;
  body: string;
}) {
  const styles = {
    info: "border-rs-info bg-rs-info-soft text-rs-info",
    warning: "border-rs-warning bg-rs-warning-soft text-rs-warning",
    success: "border-rs-success bg-rs-success-soft text-rs-success",
    error: "border-rs-danger bg-rs-danger-soft text-rs-danger",
  } as const;
  return (
    <div
      className={`rounded-rs border px-4 py-3 text-sm ${styles[tone]}`}
      role="status"
    >
      <p className="font-medium">{title}</p>
      <p className="mt-1 opacity-90">{body}</p>
    </div>
  );
}

export function StyleGuidePage() {
  const { mode, setMode } = useTheme();
  const [demoName, setDemoName] = useState("Acme Events");
  const [demoNotes, setDemoNotes] = useState("");
  const [demoRole, setDemoRole] = useState("admin");
  const [demoNotify, setDemoNotify] = useState(true);
  const [baseline] = useState({
    name: "Acme Events",
    notes: "",
    role: "admin",
    notify: true,
  });
  const [filterQ, setFilterQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dangerDialogOpen, setDangerDialogOpen] = useState(false);

  const dirty = useMemo(
    () =>
      demoName !== baseline.name ||
      demoNotes !== baseline.notes ||
      demoRole !== baseline.role ||
      demoNotify !== baseline.notify,
    [baseline, demoName, demoNotes, demoNotify, demoRole],
  );

  const nameError =
    demoName.trim().length === 0 ? "Organization name is required." : null;

  const revision = "2026-09-08";

  return (
    <div className="mx-auto max-w-[52rem]" data-testid="style-guide-root">
      <a
        href="#style-guide-main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-rs-sm focus:bg-rs-elevated focus:px-3 focus:py-2 focus:shadow-rs"
      >
        Skip to style guide content
      </a>

      <header className="space-y-3 pb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-rs-ink-subtle">
          AttendeeSync · Internal
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-rs-ink">
          Style Guide
        </h1>
        <p className="max-w-2xl text-sm text-rs-ink-muted">
          Living reference for AttendeeSync UI tokens and shared primitives.
          Source of truth:{" "}
          <code className="rounded bg-rs-muted px-1.5 py-0.5 font-mono text-[11px] text-rs-ink">
            apps/web/src/styles/tokens.css
          </code>{" "}
          and components under{" "}
          <code className="rounded bg-rs-muted px-1.5 py-0.5 font-mono text-[11px] text-rs-ink">
            apps/web/src/components/ui/
          </code>
          . Platform admin only — not linked from customer workspace chrome.
        </p>
        <div className="flex flex-wrap items-center gap-3 text-xs text-rs-ink-muted">
          <span>Content revision: {revision}</span>
          <span aria-hidden>·</span>
          <label className="inline-flex items-center gap-2">
            Theme preview
            <select
              className="h-8 rounded-rs-sm border border-rs-border bg-rs-elevated px-2 text-rs-ink"
              value={mode}
              onChange={(e) => setMode(e.target.value as ThemeMode)}
            >
              <option value="system">System</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </label>
        </div>
        <nav
          aria-label="Style guide sections"
          className="flex flex-wrap gap-2 pt-2"
        >
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="rounded-rs-sm border border-rs-border bg-rs-elevated px-2.5 py-1 text-xs text-rs-ink-muted hover:bg-rs-muted hover:text-rs-ink"
            >
              {s.label}
            </a>
          ))}
        </nav>
      </header>

      <main id="style-guide-main" className="space-y-4 pb-16">
        <Section id="sg-colors" title="Colors">
          <p className="text-sm text-rs-ink-muted">
            Solid fills from light tokens. Dark values are listed under each
            swatch for <code className="font-mono text-[11px]">.dark</code>.
            Super Admin chrome (
            <code className="font-mono text-[11px]">.rs-platform</code>) forces
            a light operational palette independently of workspace theme.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {LIGHT_SWATCHES.map((s) => (
              <Swatch
                key={s.token}
                label={s.label}
                token={s.token}
                hex={s.hex}
                requirement={`${s.requirement} Dark: ${DARK_HEX[s.token]}.`}
              />
            ))}
          </div>
        </Section>

        <Section id="sg-typography" title="Typography">
          <p className="text-sm text-rs-ink-muted">
            Font stack: <code className="font-mono text-[11px]">--rs-font</code>{" "}
            / <code className="font-mono text-[11px]">--rs-font-display</code>{" "}
            (Inter fallback stack in tokens). Prefer semantic text colors over
            raw grays.
          </p>
          <p className="text-sm text-rs-ink-muted">
            <strong>All headings are Title Case</strong> — page titles (
            <code className="font-mono text-[11px]">h1</code>), section titles (
            <code className="font-mono text-[11px]">h2</code>/
            <code className="font-mono text-[11px]">h3</code>
            ), panel titles, empty-state titles, dialog/wizard titles, and step
            headings. Capitalize major words; keep short words lowercase unless
            first or last (<code className="font-mono text-[11px]">
              a
            </code>, <code className="font-mono text-[11px]">an</code>,{" "}
            <code className="font-mono text-[11px]">the</code>,{" "}
            <code className="font-mono text-[11px]">and</code>,{" "}
            <code className="font-mono text-[11px]">or</code>,{" "}
            <code className="font-mono text-[11px]">for</code>,{" "}
            <code className="font-mono text-[11px]">to</code>,{" "}
            <code className="font-mono text-[11px]">of</code>,{" "}
            <code className="font-mono text-[11px]">in</code>,{" "}
            <code className="font-mono text-[11px]">on</code>,{" "}
            <code className="font-mono text-[11px]">at</code>,{" "}
            <code className="font-mono text-[11px]">by</code>,{" "}
            <code className="font-mono text-[11px]">with</code>,{" "}
            <code className="font-mono text-[11px]">from</code>
            ). Phrasal-verb particles stay capitalized (
            <code className="font-mono text-[11px]">Sign In</code>,{" "}
            <code className="font-mono text-[11px]">Set Up</code>
            ). Examples: Connect Your Zoom Account, Enter Credentials, Review
            and Save, Select a Conference. Body copy, descriptions, and helper
            text stay sentence case.
          </p>
          <div className="space-y-4 rounded-rs border border-rs-border bg-rs-elevated p-5">
            <p className="text-2xl font-semibold tracking-tight text-rs-ink">
              Page Title
            </p>
            <p className="text-lg font-semibold text-rs-ink">Section Title</p>
            <p className="text-base font-semibold text-rs-ink">Panel Title</p>
            <p className="text-sm text-rs-ink">
              Body copy — default UI text at 14px.
            </p>
            <p className="text-sm text-rs-ink-muted">
              Muted supporting sentence for descriptions.
            </p>
            <p className="text-xs text-rs-ink-subtle">
              Subtle meta · uppercase labels · captions
            </p>
            <p className="font-mono text-[11px] text-rs-ink-muted">
              Mono for tokens, IDs, and hex.
            </p>
          </div>
        </Section>

        <Section id="sg-buttons" title="Buttons">
          <p className="text-sm text-rs-ink-muted">
            Shared <code className="font-mono text-[11px]">Button</code> from{" "}
            <code className="font-mono text-[11px]">
              components/ui/button.tsx
            </code>
            . Button labels use <strong>Title Case</strong> — capitalize major
            words; keep short words lowercase unless first or last (
            <code className="font-mono text-[11px]">a</code>,{" "}
            <code className="font-mono text-[11px]">an</code>,{" "}
            <code className="font-mono text-[11px]">the</code>,{" "}
            <code className="font-mono text-[11px]">and</code>,{" "}
            <code className="font-mono text-[11px]">or</code>,{" "}
            <code className="font-mono text-[11px]">for</code>,{" "}
            <code className="font-mono text-[11px]">to</code>,{" "}
            <code className="font-mono text-[11px]">of</code>,{" "}
            <code className="font-mono text-[11px]">in</code>,{" "}
            <code className="font-mono text-[11px]">on</code>,{" "}
            <code className="font-mono text-[11px]">at</code>,{" "}
            <code className="font-mono text-[11px]">by</code>,{" "}
            <code className="font-mono text-[11px]">with</code>,{" "}
            <code className="font-mono text-[11px]">from</code>). Phrasal verbs
            keep the particle capitalized (
            <code className="font-mono text-[11px]">Sign In</code>,{" "}
            <code className="font-mono text-[11px]">Set Up</code>). Examples:
            Save Changes, Select a Conference, Back to Workspace, Sign In with
            Zoom. Same rule for confirm labels and empty-state actions.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="link">Link Button</Button>
            <Button disabled>Disabled</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
            <Button size="icon" aria-label="Icon Button Example">
              <CalendarDays className="h-4 w-4" />
            </Button>
          </div>
        </Section>

        <Section id="sg-action-layout" title="Action Placement">
          <p className="text-sm text-rs-ink-muted">
            Destructive actions left; Cancel immediately left of Confirm/Save;
            Confirm/Save rightmost. Dialog footers follow the same order — no
            top-right close (X).
          </p>
          <Panel>
            <PanelHeader
              title="Form Footer Anatomy"
              description="Static demo — nothing is saved."
            />
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <Button variant="danger" type="button">
                Delete
              </Button>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" type="button">
                  Cancel
                </Button>
                <Button type="button">Save Changes</Button>
              </div>
            </div>
          </Panel>
          <Panel>
            <PanelHeader
              title="List Filter Toolbar"
              description="Non-persisted demo matching platform list search patterns."
            />
            <div className="space-y-3 px-5 py-4">
              <ol className="list-decimal space-y-1 pl-5 text-xs text-rs-ink-muted">
                <li>Filter container (panel / bordered row)</li>
                <li>
                  Inline search field (no decorative icon inside the input)
                </li>
                <li>Labeled select filters</li>
                <li>Search / Apply control</li>
                <li>Clear / reset</li>
              </ol>
              <div className="flex flex-wrap items-end gap-2 rounded-rs-sm border border-rs-border bg-rs-muted p-3">
                <div className="min-w-[12rem] flex-1 space-y-1">
                  <Label htmlFor="sg-filter-q">Search</Label>
                  <Input
                    id="sg-filter-q"
                    value={filterQ}
                    onChange={(e) => setFilterQ(e.target.value)}
                    placeholder="Organization or email"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sg-filter-status">Status</Label>
                  <select
                    id="sg-filter-status"
                    className="flex h-12 rounded-rs-sm border border-rs-border bg-rs-elevated px-4 text-sm text-rs-ink"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="active">Active</option>
                    <option value="past_due">Past due</option>
                  </select>
                </div>
                <Button type="button" variant="secondary">
                  Search
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setFilterQ("");
                    setFilterStatus("");
                  }}
                >
                  Clear
                </Button>
              </div>
              <p className="text-xs text-rs-ink-subtle">
                Demo query: {filterQ || "(empty)"} · status:{" "}
                {filterStatus || "(any)"}
              </p>
            </div>
          </Panel>
        </Section>

        <Section id="sg-forms" title="Forms">
          <p className="text-sm text-rs-ink-muted">
            Text fields are oversized: <code>Input</code> is <code>h-12</code>{" "}
            with <code>px-4</code> horizontal padding (prefer the shared
            component). Textareas use the same horizontal padding with
            comfortable vertical padding. Field-level errors next to inputs; do
            not rely on HTML <code>required</code> alone. Save stays disabled
            until dirty vs loaded baseline (and while pending). Auth screens and
            the guided-setup Introduction stream-name field use placeholder text
            as the field label (with matching <code>aria-label</code>); see{" "}
            <a
              href="#sg-auth"
              className="text-rs-accent hover:text-rs-accent-hover"
            >
              Auth
            </a>{" "}
            and{" "}
            <a
              href="#sg-guided-setup"
              className="text-rs-accent hover:text-rs-accent-hover"
            >
              Guided Setup
            </a>
            .
          </p>
          <Panel className="p-5">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="sg-org-name">Organization name</Label>
                <Input
                  id="sg-org-name"
                  value={demoName}
                  aria-invalid={Boolean(nameError)}
                  aria-describedby={nameError ? "sg-org-name-error" : undefined}
                  onChange={(e) => setDemoName(e.target.value)}
                />
                {nameError ? (
                  <p id="sg-org-name-error" className="text-xs text-rs-danger">
                    {nameError}
                  </p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sg-role">Role</Label>
                <select
                  id="sg-role"
                  className="flex h-12 w-full rounded-rs-sm border border-rs-border bg-rs-elevated px-4 text-sm text-rs-ink"
                  value={demoRole}
                  onChange={(e) => setDemoRole(e.target.value)}
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sg-notes">Notes</Label>
                <textarea
                  id="sg-notes"
                  className="min-h-28 w-full rounded-rs-sm border border-rs-border bg-rs-elevated px-4 py-3 text-sm text-rs-ink placeholder:text-rs-ink-subtle"
                  value={demoNotes}
                  onChange={(e) => setDemoNotes(e.target.value)}
                  placeholder="Optional notes"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-rs-ink">
                <input
                  type="checkbox"
                  checked={demoNotify}
                  onChange={(e) => setDemoNotify(e.target.checked)}
                />
                Email me about sync failures
              </label>
              <div className="flex flex-wrap justify-end gap-2 border-t border-rs-border pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setDemoName(baseline.name);
                    setDemoNotes(baseline.notes);
                    setDemoRole(baseline.role);
                    setDemoNotify(baseline.notify);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!dirty || Boolean(nameError)}>
                  Save Changes
                </Button>
              </div>
            </form>
          </Panel>
        </Section>

        <Section id="sg-alerts" title="Alerts">
          <p className="text-sm text-rs-ink-muted">
            Inline banners use soft fills + semantic border/text. Prefer inline
            success/error copy next to the action that caused them; the app does
            not mount a toast stack.
          </p>
          <div className="space-y-3">
            <AlertDemo
              tone="info"
              title="Info"
              body="Maintenance window scheduled for Sunday 02:00 UTC."
            />
            <AlertDemo
              tone="warning"
              title="Warning"
              body="Billing is past due — sync streams are paused."
            />
            <AlertDemo
              tone="success"
              title="Success"
              body="Destination connection tested successfully."
            />
            <AlertDemo
              tone="error"
              title="Error"
              body="Zoom authorization expired. Reconnect to continue."
            />
          </div>
        </Section>

        <Section id="sg-cards" title="Cards / Panels">
          <Panel>
            <PanelHeader
              title="Panel"
              description="Default content surface: border, elevated background, shadow-rs."
              action={
                <Button size="sm" variant="secondary">
                  Action
                </Button>
              }
            />
            <div className="px-5 py-4 text-sm text-rs-ink-muted">
              Use <code className="font-mono text-[11px]">Panel</code> /{" "}
              <code className="font-mono text-[11px]">PanelHeader</code> for
              interactive or structured content. Prefer plain layout for
              marketing/hero surfaces.
            </div>
          </Panel>
        </Section>

        <Section id="sg-elevation" title="Elevation">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-rs border border-rs-border bg-rs-elevated p-4 text-sm">
              Flat elevated (border only)
            </div>
            <div className="rounded-rs border border-rs-border bg-rs-elevated p-4 text-sm shadow-rs">
              Panel elevation (
              <code className="font-mono text-[11px]">shadow-rs</code> /{" "}
              <code className="font-mono text-[11px]">--rs-shadow</code>)
            </div>
          </div>
        </Section>

        <Section id="sg-table" title="Tables">
          <p className="text-sm text-rs-ink-muted">
            Product lists use bordered rows inside panels (see platform
            organizations / conferences). Prefer subtle zebra via muted even
            rows; semantic status wins over stripe.
          </p>
          <Panel className="overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-rs-border bg-rs-muted text-xs uppercase tracking-wide text-rs-ink-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Organization</th>
                  <th className="px-4 py-3 font-medium">Conferences</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    name: "Acme Events",
                    n: 4,
                    tone: "ready" as StatusTone,
                    status: "active",
                  },
                  {
                    name: "Northwind Expo",
                    n: 1,
                    tone: "warning" as StatusTone,
                    status: "past_due",
                  },
                  {
                    name: "Contoso Summit",
                    n: 2,
                    tone: "paused" as StatusTone,
                    status: "inactive",
                  },
                ].map((row, i) => (
                  <tr
                    key={row.name}
                    className={
                      i % 2 === 1 ? "bg-rs-muted/50" : "bg-rs-elevated"
                    }
                  >
                    <td className="px-4 py-3 text-rs-ink">{row.name}</td>
                    <td className="px-4 py-3 text-rs-ink-muted">{row.n}</td>
                    <td className="px-4 py-3">
                      <StatusBadge tone={row.tone}>{row.status}</StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </Section>

        <Section id="sg-badges" title="Badges">
          <p className="text-sm text-rs-ink-muted">
            <code className="font-mono text-[11px]">StatusBadge</code> tones
            from{" "}
            <code className="font-mono text-[11px]">
              components/status/status-badge.tsx
            </code>
            .
          </p>
          <div className="flex flex-wrap gap-2">
            {STATUS_TONES.map((tone) => (
              <StatusBadge key={tone} tone={tone}>
                {tone}
              </StatusBadge>
            ))}
          </div>
        </Section>

        <Section id="sg-empty" title="Empty State">
          <EmptyState
            icon={<Inbox className="h-8 w-8" aria-hidden />}
            title="No Conferences Yet"
            description="Connect Zoom and pick an event to create your first conference sync."
            actionLabel="Start Setup"
            onAction={() => undefined}
          />
        </Section>

        <Section id="sg-loading" title="Loading">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-40" />
            </div>
            <PageSkeleton />
          </div>
        </Section>

        <Section id="sg-dialog-shell" title="Dialog Shell">
          <p className="text-sm text-rs-ink-muted">
            Use <code className="font-mono text-[11px]">ConfirmDialog</code> for
            confirmations — never raw{" "}
            <code className="font-mono text-[11px]">window.confirm</code>. Every
            dialog uses a Title Case heading that states what the modal is (e.g.{" "}
            <code className="font-mono text-[11px]">Activate Conference</code>,{" "}
            <code className="font-mono text-[11px]">Connect Zoom</code>) — same
            Title Case rules as all headings and buttons (see Typography; do not
            capitalize <code className="font-mono text-[11px]">a</code> /{" "}
            <code className="font-mono text-[11px]">the</code> / short
            prepositions unless first or last). Dismiss via footer{" "}
            <strong>Cancel</strong> + primary submit — no top-right close (X)
            control. Guided setup wizard footers differ: left red text link{" "}
            <strong>I&apos;ll Do This Later</strong>; right Back (when
            applicable) then Continue / primary.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setDialogOpen(true)}>
              Open Confirm Dialog
            </Button>
            <Button variant="danger" onClick={() => setDangerDialogOpen(true)}>
              Open Destructive Dialog
            </Button>
          </div>
          <ConfirmDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            title="Activate Conference"
            description="Billing will start for this conference. Sync streams become eligible to run."
            confirmLabel="Activate"
            onConfirm={() => setDialogOpen(false)}
          />
          <ConfirmDialog
            open={dangerDialogOpen}
            onOpenChange={setDangerDialogOpen}
            title="Offboard Organization"
            description="This cancels billing and pauses all sync for the organization."
            impact="Demo only — no API call is made from the style guide."
            confirmLabel="Offboard"
            dangerous
            onConfirm={() => setDangerDialogOpen(false)}
          />
        </Section>

        <Section id="sg-guided-setup" title="Guided Setup">
          <p className="text-sm text-rs-ink-muted">
            Multi-step setup opens as a modal over the current workspace page
            (background-location routing in{" "}
            <code className="font-mono text-[11px]">
              apps/web/src/lib/wizard-navigation.ts
            </code>
            ). Use the guided conference wizard only (`.rs-wizard-split`). All
            five steps are live in this shell: Introduction (name the sync
            stream), Setup Source (choose provider, Connect when needed, then
            upcoming events), Setup Destinations (choose CRM and enter
            credentials), Configure (field mappings and sync preferences), and
            Review (confirm and activate).
          </p>

          <div className="space-y-4 rounded-rs border border-rs-border bg-rs-elevated p-5 text-sm text-rs-ink-muted">
            <div>
              <p className="font-semibold text-rs-ink">Conference wizard</p>
              <p className="mt-1">
                Layout:{" "}
                <code className="font-mono text-[11px]">
                  layouts/wizard-layout.tsx
                </code>{" "}
                +{" "}
                <code className="font-mono text-[11px]">.rs-wizard-split</code>.
                Fixed height about{" "}
                <code className="font-mono text-[11px]">34rem</code> (capped by
                viewport). Split: dark left rail + light content pane.
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  Rail: wordmark-only brand, numbered step nav (label + short
                  description), footer line{" "}
                  <code className="font-mono text-[11px]">
                    Your event. Your systems. Connected.
                  </code>{" "}
                  — no visible “Set Up Conference” title or org name (keep a
                  visually hidden{" "}
                  <code className="font-mono text-[11px]">Dialog.Title</code>{" "}
                  for a11y).
                </li>
                <li>
                  Content pane: very subtle brand-token gradient (
                  <code className="font-mono text-[11px]">.rs-wizard-body</code>
                  : elevated → soft accent mix → muted). AttendeeSync emblem
                  watermark (
                  <code className="font-mono text-[11px]">
                    .rs-wizard-watermark
                  </code>
                  , mono, same size, low opacity) is centered in the content
                  pane; omitted on Introduction so it does not compete with the
                  sync-flow diagram. Introduction and event-select content are
                  left-aligned and scroll when needed; Connect with Zoom stays
                  centered. No scrollbar gutter unless step content overflows.
                </li>
                <li>
                  No top-right close (X). Footer: left-justified red text link{" "}
                  <strong className="text-rs-ink">
                    I&apos;ll Do This Later
                  </strong>{" "}
                  (dismisses wizard); right-justified{" "}
                  <strong className="text-rs-ink">Back</strong> when applicable,
                  then primary (e.g.{" "}
                  <strong className="text-rs-ink">Continue</strong> /{" "}
                  <strong className="text-rs-ink">Connect with Zoom</strong>).
                  Introduction: stream name required before Continue; field uses
                  placeholder-as-label (
                  <code className="font-mono text-[11px]">placeholder</code> +
                  matching{" "}
                  <code className="font-mono text-[11px]">aria-label</code>{" "}
                  <code className="font-mono text-[11px]">Stream Name</code>, no
                  visible <code className="font-mono text-[11px]">Label</code>).
                  Setup Source: choose Zoom; if Zoom is not connected, show
                  Connect with Zoom; otherwise load upcoming meetings/webinars
                  via{" "}
                  <code className="font-mono text-[11px]">
                    GET …/sources/zoom/events
                  </code>
                  , single radio-select, Continue disabled until one event is
                  chosen. Setup Destinations / Configure / Review continue in
                  the same shell (credentials, field mappings, activate).
                </li>
                <li>
                  Step headings Title Case (e.g.{" "}
                  <code className="font-mono text-[11px]">
                    Create a New Sync Stream
                  </code>
                  ,{" "}
                  <code className="font-mono text-[11px]">
                    Connect Your Zoom Account
                  </code>
                  ). Body copy stays sentence case; do not manually wrap short
                  supporting lines.
                </li>
              </ul>
            </div>
          </div>

          <div
            className="overflow-hidden rounded-rs border border-rs-border shadow-rs"
            aria-hidden="true"
          >
            <div className="grid min-h-[14rem] grid-cols-[7.5rem_minmax(0,1fr)]">
              <div className="flex flex-col bg-[#1a1e27] px-3 py-4 text-[#a3b0c2]">
                <p className="px-1 text-[11px] font-semibold tracking-tight text-[#ededec]">
                  Attendee<span className="text-[#93c5fd]">Sync</span>
                </p>
                <div className="mt-4 space-y-1 text-[10px]">
                  <div className="rounded-rs-sm bg-[#1e3a5f] px-2 py-1.5 text-[#bfdbfe]">
                    1 · Introduction
                  </div>
                  <div className="px-2 py-1.5">2 · Setup Source</div>
                  <div className="px-2 py-1.5">3 · Setup Destinations</div>
                  <div className="px-2 py-1.5">4 · Configure</div>
                  <div className="px-2 py-1.5">5 · Review</div>
                </div>
                <p className="mt-auto px-1 pt-4 text-[9px] leading-snug text-[#a3b0c2]">
                  Your event. Your systems. Connected.
                </p>
              </div>
              <div className="relative flex flex-col bg-rs-elevated p-4">
                <div className="relative z-[1] flex flex-1 flex-col justify-center gap-2 px-1">
                  <p className="text-sm font-semibold text-rs-ink">
                    Create a New Sync Stream
                  </p>
                  <p className="max-w-[36ch] text-[11px] text-rs-ink-muted">
                    Name the stream, then continue to set up the source.
                  </p>
                  <Input
                    placeholder="Stream Name"
                    aria-label="Stream Name"
                    readOnly
                    className="h-7 text-[11px]"
                  />
                </div>
                <div className="relative z-[1] flex items-center justify-between gap-2">
                  <Button
                    size="sm"
                    variant="link"
                    className="text-rs-danger"
                    type="button"
                  >
                    I&apos;ll Do This Later
                  </Button>
                  <Button size="sm" type="button">
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section id="sg-navigation" title="Navigation">
          <p className="text-sm text-rs-ink-muted">
            Real chrome primitives: workspace sidebar and Super Admin platform
            sidebar (<code className="font-mono text-[11px]">layouts/*</code>).
            Both use a sticky desktop sidebar plus a mobile/tablet drawer below
            their breakpoint (<code className="font-mono text-[11px]">md</code>{" "}
            for workspace, <code className="font-mono text-[11px]">lg</code> for
            Super Admin). Active links use{" "}
            <code className="font-mono text-[11px]">bg-rs-accent-soft</code> +{" "}
            <code className="font-mono text-[11px]">text-rs-accent-ink</code>.
            Account Menu and theme toggle live in the sidebar footer (
            <code className="font-mono text-[11px]">SidebarUserFooter</code>) —
            no workspace top header. Workspace Settings and Super Admin
            (platform admins only) live in the account menu — not sidebar nav.
            Settings tabs: Profile &amp; Appearance, Workspace, Billing.
            Workspace owners get a secondary vertical tab rail inside Settings →
            Workspace (overview, usage, audit, workspace settings). Integrations
            uses the same horizontal tab pattern as Settings/Reports: Sources,
            Destinations, Auto-Create Streams, Setup Links, Outbound Webhooks
            (default{" "}
            <code className="font-mono text-[11px]">/integrations/sources</code>
            ). Reports is a sidebar item with tabbed Activity. Billing and
            Workspace are not workspace sidebar items. Workspace sidebar header
            is wordmark-only — see{" "}
            <a
              href="#sg-brand"
              className="text-rs-accent hover:text-rs-accent-hover"
            >
              Brand
            </a>
            .
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col rounded-rs border border-rs-border bg-rs-sidebar p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-rs-ink-subtle">
                Workspace
              </p>
              <div className="space-y-0.5 text-sm">
                <div className="rounded-rs-sm bg-rs-accent-soft px-2 py-1.5 font-medium text-rs-accent-ink">
                  Dashboard
                </div>
                <div className="rounded-rs-sm px-2 py-1.5 text-rs-ink-muted">
                  Conferences
                </div>
                <div className="rounded-rs-sm px-2 py-1.5 text-rs-ink-muted">
                  Sync Streams
                </div>
                <div className="rounded-rs-sm px-2 py-1.5 text-rs-ink-muted">
                  Integrations
                </div>
                <div className="rounded-rs-sm px-2 py-1.5 text-rs-ink-muted">
                  Reports
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 border-t border-rs-border pt-2 text-xs text-rs-ink-muted">
                <span className="min-w-0 flex-1 truncate rounded-rs-sm bg-rs-muted px-2 py-1.5">
                  Account
                </span>
                <span className="rounded-rs-sm px-2 py-1.5">Theme</span>
              </div>
            </div>
            <div className="flex flex-col rounded-rs border border-rs-border bg-rs-elevated p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-rs-ink-subtle">
                Settings → Workspace
              </p>
              <div className="flex gap-3 text-sm">
                <div className="w-28 shrink-0 space-y-0.5 border-r border-rs-border pr-2">
                  <div className="rounded-rs-sm bg-rs-accent-soft px-2 py-1.5 text-xs font-medium text-rs-accent-ink">
                    Overview
                  </div>
                  <div className="rounded-rs-sm px-2 py-1.5 text-xs text-rs-ink-muted">
                    Usage
                  </div>
                  <div className="rounded-rs-sm px-2 py-1.5 text-xs text-rs-ink-muted">
                    Audit Log
                  </div>
                </div>
                <div className="min-w-0 flex-1 text-xs text-rs-ink-muted">
                  Secondary vertical tabs
                </div>
              </div>
            </div>
            <div className="rounded-rs border border-rs-border bg-[#1a1e27] p-3 text-[#ededec]">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#93a5b9]">
                Super Admin
              </p>
              <div className="space-y-0.5 text-sm">
                <div className="rounded-rs-sm bg-[#1e3a5f] px-2 py-1.5 font-medium text-[#bfdbfe]">
                  Style guide
                </div>
                <div className="rounded-rs-sm px-2 py-1.5 text-[#c2ccd9]">
                  Accounts
                </div>
                <div className="rounded-rs-sm px-2 py-1.5 text-[#c2ccd9]">
                  System Health
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section id="sg-brand" title="Brand">
          <p className="text-sm text-rs-ink-muted">
            <code className="font-mono text-[11px]">BrandMark</code> (
            <code className="font-mono text-[11px]">
              components/brand-mark.tsx
            </code>
            ) + optional SVG emblem. Wordmark is two-tone (Attendee ink / Sync
            accent); tagline “ANY EVENT. EVERY REGISTRANT.”. Styles:{" "}
            <code className="font-mono text-[11px]">.rs-brand*</code> in{" "}
            <code className="font-mono text-[11px]">styles/tailwind.css</code>.
            On dark surfaces (including the auth form rail), scope{" "}
            <code className="font-mono text-[11px]">.dark</code> so the wordmark
            uses dark-mode tokens — do not invent one-off wordmark colors.
          </p>
          <Panel className="space-y-3 p-5">
            <p className="text-sm font-medium text-rs-ink">
              Wordmark size scale
            </p>
            <ul className="list-disc space-y-1.5 pl-5 text-sm text-rs-ink-muted">
              <li>
                <strong>sm / md:</strong> name 18px — default product chrome
                (workspace sidebar, platform header, compact layouts).
              </li>
              <li>
                <strong>lg:</strong> name 28px (~1.56× base) — large marks; icon
                40px when shown.
              </li>
              <li>
                <strong>Auth</strong> (
                <code className="font-mono text-[11px]">.rs-auth-wordmark</code>
                ): name 32px (~1.78× base) — form-rail hero; always{" "}
                <code className="font-mono text-[11px]">
                  showIcon=&#123;false&#125;
                </code>{" "}
                (emblem is separate on the left column).
              </li>
              <li>
                <strong>Emblem (when shown):</strong> sm 24px · md 28px · lg
                40px.
              </li>
              <li>
                <strong>Workspace sidebar:</strong> wordmark-only (
                <code className="font-mono text-[11px]">
                  &lt;BrandMark showIcon=&#123;false&#125; /&gt;
                </code>
                ).
              </li>
            </ul>
          </Panel>
          <div className="flex flex-wrap items-end gap-8 rounded-rs border border-rs-border bg-rs-elevated p-5">
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-rs-ink-subtle">
                sm · 18px
              </p>
              <BrandMark size="sm" />
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-rs-ink-subtle">
                md · 18px
              </p>
              <BrandMark size="md" />
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-rs-ink-subtle">
                lg · 28px
              </p>
              <BrandMark size="lg" />
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-rs-ink-subtle">
                Wordmark only
              </p>
              <BrandMark showIcon={false} />
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-rs-ink-subtle">
                Mono (dark)
              </p>
              <div className="dark rounded-rs-sm bg-[#1a1e27] p-4">
                <BrandMark size="sm" iconVariant="mono" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-rs-ink-subtle">
                Auth · 32px
              </p>
              <div className="dark rounded-rs-sm bg-gradient-to-br from-[#1e4fd6] to-[#0e70c4] p-4">
                <BrandMark
                  size="lg"
                  showIcon={false}
                  className="rs-auth-wordmark"
                />
              </div>
            </div>
          </div>
        </Section>

        <Section id="sg-auth" title="Auth">
          <p className="text-sm text-rs-ink-muted">
            Shared shell:{" "}
            <code className="font-mono text-[11px]">AuthShell</code> (
            <code className="font-mono text-[11px]">
              features/auth/auth-shell.tsx
            </code>
            ). Styles live under{" "}
            <code className="font-mono text-[11px]">.rs-auth*</code> in{" "}
            <code className="font-mono text-[11px]">styles/tailwind.css</code>.
          </p>
          <Panel className="space-y-4 p-5">
            <ul className="list-disc space-y-2 pl-5 text-sm text-rs-ink-muted">
              <li>
                Full-bleed split: light left with large emblem; brand-blue
                gradient form rail on the right with dark-mode wordmark (32px)
                above the form — see{" "}
                <a
                  href="#sg-brand"
                  className="text-rs-accent hover:text-rs-accent-hover"
                >
                  Brand
                </a>{" "}
                size scale.
              </li>
              <li>
                Product version badge bottom-left (
                <code className="font-mono text-[11px]">v</code>+ semver from
                root <code className="font-mono text-[11px]">package.json</code>
                ).
              </li>
              <li>
                Auth fields: no visible labels — placeholder is the label; keep
                a matching{" "}
                <code className="font-mono text-[11px]">aria-label</code>.
              </li>
              <li>
                Login actions: <strong>Forgot Password</strong> right-justified
                under the password field (
                <code className="font-mono text-[11px]">.rs-auth-forgot</code>);
                primary <strong>Sign In</strong> is a full-width pill (
                <code className="font-mono text-[11px]">
                  .rs-auth-submit.rs-auth-submit-block
                </code>
                ).
              </li>
            </ul>
            <div className="mx-auto w-full max-w-xs space-y-3 rounded-rs border border-rs-border bg-rs-muted p-4">
              <Input placeholder="Email" aria-label="Email" readOnly />
              <div className="space-y-1.5">
                <Input
                  placeholder="Password"
                  aria-label="Password"
                  type="password"
                  readOnly
                />
                <p className="rs-auth-forgot text-rs-accent">Forgot Password</p>
              </div>
              <Button
                className="rs-auth-submit rs-auth-submit-block w-full"
                type="button"
              >
                Sign In
              </Button>
            </div>
          </Panel>
        </Section>

        <Section id="sg-a11y" title="Accessibility">
          <ul className="list-disc space-y-2 pl-5 text-sm text-rs-ink-muted">
            <li>
              Global focus ring:{" "}
              <code className="font-mono text-[11px]">*:focus-visible</code>{" "}
              uses <code className="font-mono text-[11px]">--rs-focus</code>.
            </li>
            <li>
              Dialogs use Radix Dialog with titled content; dismiss via footer
              Cancel + primary action only — never a top-right close (X) button.
            </li>
            <li>
              Status badges include an{" "}
              <code className="font-mono text-[11px]">sr-only</code> “Status:”
              prefix.
            </li>
            <li>
              Prefer semantic alerts (
              <code className="font-mono text-[11px]">
                role=&quot;status&quot;
              </code>
              ) for banners.
            </li>
            <li>
              Do not ship user-facing{" "}
              <code className="font-mono text-[11px]">alert()</code> /{" "}
              <code className="font-mono text-[11px]">confirm()</code>.
            </li>
          </ul>
        </Section>
      </main>
    </div>
  );
}
